import { useState } from "react";
import * as Animate from "react-reveal";
import { ArrowUpRight, ArrowRight } from "../../../components/svgs";

const steps = [
  {
    key: "goal",
    number: "01",
    label: "Your goal",
    placeholder: "Choose a goal",
    options: [
      "Start a technology career",
      "Change my current career",
      "Advance in my profession",
      "Build skills for my business",
      "Prepare for leadership",
      "Learn AI and automation",
    ],
  },
  {
    key: "experience",
    number: "02",
    label: "Your experience",
    placeholder: "Choose your level",
    options: [
      "I am completely new",
      "I have some knowledge",
      "I already work in the field",
      "I am a manager or business leader",
    ],
  },
  {
    key: "format",
    number: "03",
    label: "Learning format",
    placeholder: "Choose a format",
    options: [
      "Live online",
      "Self-paced",
      "Short intensive program",
      "Complete career bootcamp",
    ],
  },
];

const recommendations = [
  {
    title: "Data Analytics Bootcamp",
    detail: "Beginner-friendly · Live online · $300",
  },
  {
    title: "Product Management & Design",
    detail: "Beginner-friendly · Live online · $400",
  },
  {
    title: "Cybersecurity Bootcamp",
    detail: "Beginner-friendly · Live online · $500",
  },
];

const FinderSelect = ({
  number,
  label,
  placeholder,
  options,
  value,
  onChange,
}) => (
  <div>
    <p className="flex items-center gap-2 font-poppins text-xs font-bold text-primary mb-2">
      <span className="w-6 h-6 rounded-full bg-hire-tint-blue text-primary grid place-items-center text-2xs flex-none">
        {number}
      </span>
      {label}
    </p>
    <div className="relative">
      <select
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className={`w-full h-12 appearance-none rounded-xl border border-hire-line bg-white font-poppins text-sm px-4 pr-10 transition-colors focus:outline-none focus:border-secondary focus:ring-2 focus:ring-secondary ${
          value ? "text-default font-semibold" : "text-gray"
        }`}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <svg
        className="w-4 h-4 absolute right-4 top-1/2 -translate-y-1/2 text-gray pointer-events-none"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  </div>
);

const HomeFinderSection = () => {
  const [answers, setAnswers] = useState({
    goal: null,
    experience: null,
    format: null,
  });
  const [submitted, setSubmitted] = useState(false);

  const select = (key, option) => {
    setSubmitted(false);
    setAnswers((prev) => ({ ...prev, [key]: option }));
  };

  const canSubmit = steps.every((step) => Boolean(answers[step.key]));

  return (
    <section
      className="hire-grid-bg text-white py-16 md:py-24 px-4"
      id="finder"
    >
      <div className="container mx-auto grid home-grid-0713 items-center gap-10">
        <Animate.Fade bottom>
          <div className="lg:sticky lg:top-[130px]">
            <p className="flex items-center font-poppins gap-3 text-gray-30 font-extrabold tracking-[0.1em] uppercase mb-5">
              <span className="w-8 h-[3px] bg-secondary inline-block"></span>
              Program finder
            </p>
            <h2 className="text-white  font-poppins font-semibold tracking-[-0.045em] leading-[1.12] text-3xl md:text-4xl lg:text-6xl mb-5">
              Find the right program for you.
            </h2>
            <p className="text-[#d6ddff] font-poppins text-base leading-[1.8]">
              Three quick answers. A clearer place to start.
            </p>
          </div>
        </Animate.Fade>

        <Animate.Fade bottom>
          <div className="bg-white text-default rounded-[26px] p-6 md:p-9 shadow-course-feature-lg">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:items-end gap-4">
              {steps.map((step) => (
                <div key={step.key} className="lg:flex-1 lg:min-w-0">
                  <FinderSelect
                    number={step.number}
                    label={step.label}
                    placeholder={step.placeholder}
                    options={step.options}
                    value={answers[step.key]}
                    onChange={(option) => select(step.key, option)}
                  />
                </div>
              ))}

              <div className="flex flex-col justify-end lg:flex-none">
                <p
                  className="hidden lg:flex items-center gap-2 invisible mb-2"
                  aria-hidden="true"
                >
                  <span className="w-6 h-6 flex-none" />
                </p>
                <button
                  type="button"
                  disabled={!canSubmit}
                  onClick={() => setSubmitted(true)}
                  className={`rounded-xl h-12 font-poppins inline-flex items-center justify-center gap-3 px-6 text-sm font-bold whitespace-nowrap transition-all ${
                    canSubmit
                      ? "bg-primary text-white hover:-translate-y-0.5 shadow-course-feature"
                      : "bg-[#9aa5c8] text-white cursor-not-allowed"
                  }`}
                >
                  See Matches{" "}
                  <span aria-hidden="true">
                    <ArrowRight fill="currentColor" />
                  </span>
                </button>
              </div>
            </div>

            {submitted && canSubmit && (
              <div className="bg-hire-tint-yellow rounded-2xl mt-6 p-5">
                <span className="text-[#8a5c00] font-poppins tracking-[0.08em] text-xs font-extrabold">
                  YOUR STARTING POINTS
                </span>
                <h3 className="text-primary text-xl font-poppins font-semibold mt-1.5 mb-3.5">
                  Three programs worth comparing
                </h3>
                <div className="grid gap-2">
                  {recommendations.map((program) => (
                    <a
                      key={program.title}
                      href="https://study.utiva.co/"
                      target="_blank"
                      rel="noreferrer noopener"
                      className="bg-white border border-[#ead8a5] font-poppins rounded-[10px] grid home-grid-quiz-result gap-x-2.5 gap-y-1 p-3.5"
                    >
                      <strong className="text-primary text-sm">
                        {program.title}
                      </strong>
                      <b className="text-primary row-span-2 self-center font-poppins">
                        <ArrowUpRight className="w-3.5 h-3.5 inline-block" />
                      </b>
                      <small className="text-gray text-sm font-poppins">
                        {program.detail}
                      </small>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Animate.Fade>
      </div>
    </section>
  );
};

export default HomeFinderSection;
